import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Loader2, Route, RefreshCw, Swords, GraduationCap, MessageSquareText,
  CheckCircle2, Circle, ArrowRight, Target, Sparkles, AlertTriangle
} from "lucide-react";

const TYPE = {
  roleplay: { icon: Swords, label: "Roleplay", cta: "Practice now", cls: "bg-emerald-500/10 border-emerald-500/20 text-emerald-600" },
  lesson: { icon: GraduationCap, label: "Lesson", cta: "Open lesson", cls: "bg-cyan-500/10 border-cyan-500/20 text-cyan-600" },
  coach: { icon: MessageSquareText, label: "Coach", cta: "Ask Linden", cls: "bg-amber-500/10 border-amber-500/20 text-amber-600" },
};

function StepCard({ step, index, onGo, onToggle }) {
  const t = TYPE[step.type];
  return (
    <motion.div initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.05 }}
      className={`card-surface rounded-2xl p-4 sm:p-5 flex gap-4 ${step.done ? "opacity-70" : ""}`} data-testid={`plan-step-${step.id}`}>
      <button onClick={() => onToggle(step)} className="mt-0.5 text-slate-400 hover:text-emerald-600 transition-colors shrink-0" data-testid={`plan-step-toggle-${step.id}`} title={step.done ? "Mark as not done" : "Mark as done"}>
        {step.done ? <CheckCircle2 className="w-6 h-6 text-emerald-500" /> : <Circle className="w-6 h-6" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border ${t.cls}`}>
            <t.icon className="w-3 h-3" /> {t.label}
          </span>
          {step.type === "roleplay" && step.target_score && <span className="text-[10px] font-mono text-slate-500">Target {step.target_score}+</span>}
          {step.best_score != null && <span className="text-[10px] font-mono text-emerald-600">Best {step.best_score}</span>}
        </div>
        <div className={`font-semibold text-slate-900 mt-1.5 ${step.done ? "line-through" : ""}`}>{step.title}</div>
        <p className="text-sm text-slate-500 mt-1 leading-snug">{step.why}</p>
        {step.type === "coach" && <p className="text-xs text-slate-600 italic mt-1.5">“{step.prompt}”</p>}
      </div>
      {step.type === "roleplay" && step.persona_avatar && (
        <img src={step.persona_avatar} alt="" className="hidden sm:block w-12 h-12 rounded-full object-cover border border-slate-200 shrink-0" />
      )}
      <Button onClick={() => onGo(step)} size="sm" variant="outline" className="self-center shrink-0 border-slate-300 bg-white text-slate-900 hover:bg-slate-50" data-testid={`plan-step-go-${step.id}`}>
        {t.cta} <ArrowRight className="w-3.5 h-3.5 ml-1" />
      </Button>
    </motion.div>
  );
}

export default function Plan() {
  const navigate = useNavigate();
  const [plan, setPlan] = useState(undefined);
  const [generating, setGenerating] = useState(false);

  useEffect(() => { api.get("/plan").then((r) => setPlan(r.data.plan)).catch(() => setPlan(null)); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/plan/generate");
      setPlan(data.plan);
      toast.success("Your plan is ready");
    } catch { toast.error("Linden couldn't build your plan. Try again."); }
    finally { setGenerating(false); }
  };

  const go = (step) => {
    if (step.type === "roleplay") navigate(`/app/roleplay?persona=${step.persona_id}`);
    else if (step.type === "lesson") navigate("/app/courses");
    else navigate(`/app/coach?q=${encodeURIComponent(step.prompt)}`);
  };

  const toggle = async (step) => {
    try { const { data } = await api.post(`/plan/steps/${step.id}/toggle`); setPlan(data.plan); }
    catch { toast.error("Couldn't update step"); }
  };

  if (plan === undefined) return <div className="flex items-center justify-center py-32 text-emerald-600"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="plan-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-emerald-600 font-mono text-xs uppercase tracking-widest">Built by Linden · from your scores</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-1">Your training plan</h1>
        </div>
        {plan && (
          <Button onClick={generate} disabled={generating} variant="outline" className="border-slate-300 bg-white text-slate-900 hover:bg-slate-50" data-testid="plan-regenerate-btn">
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <RefreshCw className="w-4 h-4 mr-1.5" />} Rebuild plan
          </Button>
        )}
      </div>

      {!plan && (
        <div className="card-surface rounded-3xl p-8 sm:p-12 text-center" data-testid="plan-empty">
          <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto"><Route className="w-8 h-8 text-emerald-600" /></div>
          <h2 className="text-xl font-bold text-slate-900 mt-5">Let Linden build your path</h2>
          <p className="text-slate-500 mt-2 max-w-md mx-auto text-sm">Linden reads your roleplay scores, the customer types you struggle with and your lesson progress — then hands you a six-step plan that targets exactly what's holding you back.</p>
          <Button onClick={generate} disabled={generating} className="mt-6 bg-emerald-500 hover:bg-emerald-600 text-black font-semibold h-11 px-6" data-testid="plan-generate-btn">
            {generating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Linden is reviewing your sessions…</> : <><Sparkles className="w-4 h-4 mr-2" /> Build my plan</>}
          </Button>
        </div>
      )}

      {plan && (
        <>
          {plan.stale && (
            <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-700 text-sm" data-testid="plan-stale-banner">
              <AlertTriangle className="w-4 h-4 shrink-0" /> You've completed several sessions since this plan was built — rebuild it to re-target your weak spots.
            </div>
          )}
          <div className="card-surface rounded-3xl p-6 sm:p-8" data-testid="plan-headline-card">
            <div className="flex items-start gap-3">
              <Target className="w-5 h-5 text-emerald-600 mt-1 shrink-0" />
              <div>
                <h2 className="text-lg md:text-xl font-bold text-slate-900 leading-snug" data-testid="plan-headline">{plan.headline}</h2>
                <p className="text-slate-600 mt-3 leading-relaxed text-sm sm:text-base">{plan.diagnosis}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-5">
              {(plan.focus_areas || []).map((f) => (
                <span key={f} className="px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 text-xs font-medium" data-testid="plan-focus-chip">{f}</span>
              ))}
            </div>
            <div className="mt-6">
              <div className="flex justify-between text-xs text-slate-500 font-mono mb-2">
                <span>Progress</span><span data-testid="plan-progress-text">{plan.progress.done}/{plan.progress.total} steps</span>
              </div>
              <Progress value={(plan.progress.done / Math.max(1, plan.progress.total)) * 100} className="h-2 bg-slate-100" />
            </div>
          </div>

          <div className="space-y-3">
            {plan.steps.map((s, i) => <StepCard key={s.id} step={s} index={i} onGo={go} onToggle={toggle} />)}
          </div>
        </>
      )}
    </div>
  );
}
